import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Calendar, Clock, User, ArrowRight, ShieldCheck, HelpCircle } from 'lucide-react';
import { blogPosts } from '../components/Blog';
import '../components/BlogPost.css';

// Helper to clean HTML, removing script/style tags and their inner content entirely
const cleanHtmlText = (html) => {
  if (!html) return '';
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<[^>]+>/g, '')
    .trim();
};

export default function BlogPostPage() {
  const { slug } = useParams();
  
  const [post, setPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    // 1. Try to find the exact post by slug from local fallback
    const localPost = blogPosts.find((p) => p.slug === slug);
    if (localPost) {
      setPost(localPost);
      setIsLoading(false);
      return;
    }

    // 2. If not found, fetch from Blogger
    const fetchBloggerFeedJSONP = () => {
      setIsLoading(true);
      const callbackName = 'blogger_post_callback_' + Math.floor(Math.random() * 1000000);
      
      window[callbackName] = (data) => {
        cleanup();
        try {
          if (data && data.feed && data.feed.entry && data.feed.entry.length > 0) {
            // Find the post matching the slug
            const entry = data.feed.entry.find(e => {
              const alternateLink = e.link.find(l => l.rel === 'alternate');
              if (!alternateLink) return false;
              const entrySlug = alternateLink.href.split('/').pop().replace('.html', '');
              return entrySlug === slug;
            });

            if (entry) {
              const title = entry.title.$t;
              const rawContent = entry.content ? entry.content.$t : '';
              
              // Extract first image from HTML content to use as cover thumbnail
              const imgRegex = /<img[^>]+src="([^">]+)"/;
              const imgMatch = rawContent.match(imgRegex);
              const coverImage = imgMatch ? imgMatch[1] : (entry.media$thumbnail ? entry.media$thumbnail.url : null);
              
              // Excerpt extraction
              let excerpt = entry.summary ? cleanHtmlText(entry.summary.$t) : '';
              if (!excerpt && rawContent) {
                excerpt = cleanHtmlText(rawContent);
              }
              if (excerpt.length > 160) {
                excerpt = excerpt.substring(0, 160).trim() + '...';
              }
              
              // Date formatting
              const pubDate = new Date(entry.published.$t);
              const date = pubDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
              
              // Tags and category sorting
              const tags = entry.category ? entry.category.map(c => c.term) : ['Insights'];
              let category = 'Cloud & DevOps';
              const lowerTags = tags.map(t => t.toLowerCase());
              if (lowerTags.some(t => t.includes('security') || t.includes('cyber') || t.includes('iso'))) {
                category = 'Cybersecurity';
              } else if (lowerTags.some(t => t.includes('ai') || t.includes('automation') || t.includes('ml') || t.includes('rpa'))) {
                category = 'AI & Automation';
              } else if (lowerTags.some(t => t.includes('software') || t.includes('dev') || t.includes('code') || t.includes('aws') || t.includes('kubernetes'))) {
                category = 'Custom Software';
              } else if (lowerTags.some(t => t.includes('case') || t.includes('study') || t.includes('costs'))) {
                category = 'Case Studies';
              } else if (tags[0]) {
                category = tags[0]; 
              }
              
              const wordsCount = rawContent.split(/\s+/).length;
              const readTime = `${Math.max(3, Math.ceil(wordsCount / 200))} min read`;

              setPost({
                slug,
                title,
                excerpt,
                category,
                author: entry.author && entry.author[0] && entry.author[0].name ? entry.author[0].name.$t : 'Wayug Contributor',
                role: 'Consultant',
                date,
                readTime,
                tags,
                accentColor: '#06b6d4',
                coverGradient: 'linear-gradient(135deg, #0e7490 0%, #06b6d4 100%)',
                coverImage,
                contentHtml: rawContent, // store raw HTML for dynamic rendering
                isDynamic: true
              });
            } else {
              setIsError(true);
            }
          } else {
            setIsError(true);
          }
        } catch (err) {
          setIsError(true);
        } finally {
          setIsLoading(false);
        }
      };

      const script = document.createElement('script');
      script.src = `https://blog.wayug.com/feeds/posts/default?alt=json-in-script&callback=${callbackName}`;
      script.async = true;
      script.id = 'blogger-post-script';
      
      const cleanup = () => {
        const existingScript = document.getElementById('blogger-post-script');
        if (existingScript) existingScript.remove();
        delete window[callbackName];
      };

      const timer = setTimeout(() => {
        cleanup();
        setIsError(true);
        setIsLoading(false);
      }, 5000);

      script.onload = () => clearTimeout(timer);
      script.onerror = () => {
        clearTimeout(timer);
        cleanup();
        setIsError(true);
        setIsLoading(false);
      };

      document.head.appendChild(script);

      return () => {
        clearTimeout(timer);
        cleanup();
      };
    };

    fetchBloggerFeedJSONP();
  }, [slug]);

  if (isLoading) {
    return (
      <div className="blog-post-page-empty" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="text-center">
          <div className="loading-spinner" style={{ border: '3px solid rgba(255,255,255,0.05)', borderTop: '3px solid #06b6d4', borderRadius: '50%', width: '36px', height: '36px', animation: 'spin 0.8s linear infinite', margin: '0 auto 1.5rem' }}></div>
          <p style={{ color: '#737373', fontSize: '0.92rem', letterSpacing: '0.02em' }}>Loading article...</p>
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  // If post doesn't exist, show a clean, user-friendly 404 message
  if (!post || isError) {
    return (
      <div className="blog-post-page-empty">
        <div className="container text-center">
          <HelpCircle size={64} className="error-icon" />
          <h1>Article Not Found</h1>
          <p>The technical guide or case study you are looking for might have been moved or updated.</p>
          <Link to="/blog" className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            <ArrowLeft size={16} /> Return to Blog
          </Link>
        </div>
      </div>
    );
  }

  // Pick related posts (excluding current post, filter by same category if possible, otherwise pick first 2)
  const sameCategoryPosts = blogPosts.filter((p) => p.category === post.category && p.slug !== post.slug);
  const relatedPosts = sameCategoryPosts.length >= 2 
    ? sameCategoryPosts.slice(0, 2) 
    : blogPosts.filter((p) => p.slug !== post.slug).slice(0, 2);

  // Create customized WhatsApp CTA message
  const whatsappCTAText = `Hi Wayug! I just read your article "${post.title}" and would like to schedule a call to discuss our technology requirements.`;
  const whatsappUrl = `https://wa.me/918287999236?text=${encodeURIComponent(whatsappCTAText)}`;

  // Define structured JSON-LD schemas for this specific article
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://wayug.com/' },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://wayug.com/blog' },
      { '@type': 'ListItem', position: 3, name: post.title, item: `https://wayug.com/blog/${post.slug}` },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    '@id': `https://wayug.com/blog/${post.slug}#article`,
    isPartOf: {
      '@type': 'WebPage',
      '@id': `https://wayug.com/blog/${post.slug}`,
      url: `https://wayug.com/blog/${post.slug}`,
      name: post.title,
    },
    headline: post.title,
    description: post.excerpt,
    image: 'https://wayug.com/logofull.png',
    datePublished: '2026-05-24', // Standardized for alignment
    dateModified: '2026-05-28',
    author: {
      '@type': 'Person',
      name: post.author,
      jobTitle: post.role,
      worksFor: {
        '@type': 'Organization',
        name: 'Wayug Infotech Solutions',
        url: 'https://wayug.com',
      },
    },
    publisher: {
      '@type': 'Organization',
      name: 'Wayug Infotech Solutions',
      logo: {
        '@type': 'ImageObject',
        url: 'https://wayug.com/logofull.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://wayug.com/blog/${post.slug}`,
    },
  };

  return (
    <div className="blog-post-page">
      <Helmet>
        {/* ── Page Title & Description ── */}
        <title>{post.title} | Wayug Engineering Blog</title>
        <meta name="description" content={post.excerpt} />
        <link rel="canonical" href={`https://wayug.com/blog/${post.slug}`} />

        {/* ── Open Graph Article Tags ── */}
        <meta property="og:title" content={`${post.title} | Wayug Blog`} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:url" content={`https://wayug.com/blog/${post.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="article:published_time" content="2026-05-24T08:00:00+05:30" />
        <meta property="article:author" content={post.author} />
        <meta property="article:section" content={post.category} />
        <meta property="og:image" content="https://wayug.com/logofull.png" />

        {/* ── Schemas ── */}
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      <article className="post-container container">
        
        {/* Navigation and Back button */}
        <div className="post-navigation">
          <Link to="/blog" className="back-link">
            <ArrowLeft size={16} /> Back to Blog
          </Link>
        </div>

        {/* Header Section */}
        <header className="post-header-wrap">
          <span className="post-category-tag" style={{ color: post.accentColor, backgroundColor: `${post.accentColor}18` }}>
            {post.category}
          </span>
          <h1 className="post-headline">{post.title}</h1>
          <p className="post-lead-excerpt">{post.excerpt}</p>

          <div className="post-meta-row">
            <div className="author-block">
              <div className="author-avatar" style={{ backgroundColor: `${post.accentColor}25`, color: post.accentColor }}>
                <User size={16} />
              </div>
              <div>
                <span className="author-name">{post.author}</span>
                <span className="author-role">{post.role}</span>
              </div>
            </div>
            <div className="meta-time-block">
              <span className="meta-item"><Calendar size={14} /> {post.date}</span>
              <span className="meta-item"><Clock size={14} /> {post.readTime}</span>
            </div>
          </div>
        </header>

        {/* Hero Visual Cover */}
        <div className="post-cover-image" style={{ background: post.coverGradient }}>
          <div className="cover-grid-shimmer" />
        </div>

        {/* Main Content Body */}
        {post.isDynamic ? (
          <div className="post-body-content" dangerouslySetInnerHTML={{ __html: post.contentHtml }} />
        ) : (
          <div className="post-body-content">
            {post.content.map((sec, idx) => {
              switch (sec.type) {
                case 'paragraph':
                  return <p key={idx} className="content-p">{sec.text}</p>;
                case 'heading':
                  return <h2 key={idx} className="content-h2">{sec.text}</h2>;
                case 'list':
                  return (
                    <ul key={idx} className="content-ul">
                      {sec.items.map((item, itemIdx) => (
                        <li key={itemIdx} className="content-li">{item}</li>
                      ))}
                    </ul>
                  );
                case 'code':
                  return (
                    <div key={idx} className="content-code-wrap">
                      <pre className="code-pre">
                        <code className="code-block">{sec.code}</code>
                      </pre>
                    </div>
                  );
                case 'quote':
                  return (
                    <blockquote key={idx} className="content-blockquote">
                      <p>"{sec.text}"</p>
                    </blockquote>
                  );
                default:
                  return null;
              }
            })}
          </div>
        )}

        {/* Dynamic Contextual WhatsApp CTA Banner */}
        <div className="post-cta-banner">
          <div className="cta-content">
            <ShieldCheck className="cta-icon" size={36} />
            <div>
              <h3>Need help applying these engineering strategies to your stack?</h3>
              <p>Schedule a technical assessment with Amit and Siddharth. We will review your current infrastructure, compliance bottlenecks, and automation plans.</p>
            </div>
          </div>
          <a 
            href={whatsappUrl}
            target="_blank" 
            rel="noopener noreferrer" 
            className="btn btn-primary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', alignSelf: 'center', flexShrink: 0 }}
          >
            Discuss on WhatsApp <ArrowRight size={16} />
          </a>
        </div>

        {/* Author Bio Section */}
        <div className="post-author-bio-section">
          <div className="bio-avatar" style={{ backgroundColor: `${post.accentColor}25`, color: post.accentColor }}>
            <User size={32} />
          </div>
          <div className="bio-info">
            <h4>About {post.author}</h4>
            <p className="bio-role">{post.role} at Wayug Infotech Solutions</p>
            <p className="bio-text">
              Lead systems architect and engineer dedicated to building highly secure, cloud-native infrastructures, zero-trust cybersecurity perimeters, and compliance auditing protocols for Indian enterprises.
            </p>
          </div>
        </div>

        {/* ── Read Next (Related Articles) ── */}
        <div className="post-read-next-section">
          <h3 className="section-label-related">What to read next</h3>
          <div className="related-grid">
            {relatedPosts.map((relatedPost) => (
              <Link to={`/blog/${relatedPost.slug}`} key={relatedPost.slug} className="related-card-link">
                <div className="related-card">
                  <div className="related-cover" style={{ background: relatedPost.coverGradient }}>
                    <span className="related-cat-badge">{relatedPost.category}</span>
                  </div>
                  <div className="related-body">
                    <h4>{relatedPost.title}</h4>
                    <p className="related-excerpt">{relatedPost.excerpt}</p>
                    <span className="related-read-more" style={{ color: relatedPost.accentColor }}>
                      Read Article <ArrowRight size={14} />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

      </article>
    </div>
  );
}
